/**
 * Wie groß ein Bewertungsprompt ist — und ab wann das gemeldet gehört.
 *
 * EINE Stelle für beide Seiten. Die Reflexionsseite zeigt die Maße des Prompts,
 * an den der nächste Vorschlag geht; die Versionsseite zeigt sie je Version.
 * Zwei Zählungen für dieselbe Sache wären zwei Wahrheiten, und eine davon liefe
 * irgendwann anders — genau der Fehler vom 10.08., als das Frontend eine eigene
 * `MIN_REFERENZ_ZELLEN = 10` gegen eine Backend-Schwelle rechnete, die längst
 * je Umgebung verstellbar war.
 *
 * ACHTUNG, was diese Warnung NICHT ist: eine Tokenmessung. Gezählt werden
 * Zeichen im Browser. Die harte Grenze ist `num_ctx` im Ollama-Dienst, und die
 * ist von hier aus nicht zu sehen. Wer sie wissen will, liest das Serverlog.
 *
 * ACHTUNG, keine Sperre. Entschieden wurde am 11.08. ausdrücklich für
 * Sichtbarkeit statt Schranke: ein Prompt darf wachsen, es soll nur niemand
 * dabei zusehen müssen, ohne es zu merken.
 */

// Das Muster einer nummerierten Regelzeile. Wortgleich zu REGEL_MUSTER in
// rta-ui/backend/app/routers/reflection.py und in rta-async-backend
// app/services/reflection.py. Zählte diese Seite anders als der Code, der die
// Regeln einsetzt, stünde neben dem Prompt eine Zahl, die nicht zu ihm gehört.
const REGEL_MUSTER = /^\s*(\d{1,3})[.)]\s/;

/**
 * Ab welcher Zeichenzahl gewarnt wird.
 *
 * Hergeleitet aus einer Messung, nicht gesetzt: am 11.08. belegte der FERTIG
 * GEFÜLLTE RAG-Bewertungsprompt 2925 von 4096 Token (`truncated 0`,
 * gemessen am 11.08.). Rest also 1171 Token, 29 %.
 * Diesen Rest tragen zur Laufzeit `{criteria}`, `{rag_results}` und im
 * LLM-Pfad der Gesprächsverlauf (bei RATING_CONTEXT_K=5 allein bis ~750 Token)
 * — er wächst mit den Daten, nicht mit dem Prompt. Die Hälfte des Rests dem
 * Template zugestanden sind ~2300 Zeichen über die heutigen 1135; aufgerundet
 * 3500.
 */
export const PROMPT_WARN_ZEICHEN = 3500;

/**
 * Ab wie vielen Regeln gewarnt wird.
 *
 * ACHTUNG, das ist ausdrücklich KEINE Messung, und der Hilfetext sagt es auch.
 * Es ist eine Konvention gegen eine RATE: der Kreislauf hat in fünf Tagen elf
 * auf zwölf Regeln gebracht, und die zwölfte widersprach der vierten. Ab hier
 * lohnt Überarbeiten mehr als Anhängen.
 */
export const REGELN_WARN = 15;

export const HILFE_REGELN =
  "Wie viele nummerierte Regeln dieser Prompt führt. Die Schwelle von " +
  REGELN_WARN +
  " ist eine Konvention, keine Messung: der Kreislauf hat in fünf Tagen elf auf " +
  "zwölf Regeln gebracht, und die zwölfte riet vom Vergeben einer Zahl ab — " +
  "was den Regeln 4 und 7 desselben Prompts widerspricht. Ab hier lohnt " +
  "Überarbeiten mehr als Anhängen.";

export const HILFE_ZEICHEN =
  "Die Länge des Prompt-Templates. Gewarnt wird ab " +
  PROMPT_WARN_ZEICHEN +
  " Zeichen, und das ist gemessen: am 11.08. belegte der fertig gefüllte " +
  "Bewertungsprompt 2925 von 4096 Token. Den Rest des Fensters tragen " +
  "Kriterien, RAG-Treffer und Gesprächsverlauf — sie wachsen mit den Daten, " +
  "nicht mit dem Prompt. Ollama schneidet einen zu langen Prompt ab, ohne es " +
  "zu melden; die Antwort sieht danach aus wie eine vollständige. " +
  "Gezählt werden hier Zeichen, keine Token — die harte Grenze steht im " +
  "Ollama-Serverlog.";

/** Wie viele verschiedene Regelnummern der Prompt führt. */
export function regelAnzahl(content) {
  const nummern = new Set();
  for (const zeile of String(content || "").split(/\r?\n/)) {
    const treffer = REGEL_MUSTER.exec(zeile);
    if (treffer) nummern.add(Number(treffer[1]));
  }
  return nummern.size;
}

/** Die Länge des Prompt-Templates in Zeichen. */
export function promptZeichen(content) {
  return String(content || "").length;
}

/**
 * Was an diesem Prompt zu melden ist — oder "".
 *
 * Leer heißt: nichts sagen. Ein Hinweis, der immer erscheint, wird nicht
 * gelesen; auf dem heutigen Bestand (1135 Zeichen, 11 Regeln) muss diese
 * Funktion deshalb "" liefern, und genau das hält ein Test fest.
 */
export function promptWarnung(content) {
  const regeln = regelAnzahl(content);
  const zeichen = promptZeichen(content);
  const teile = [];
  if (regeln >= REGELN_WARN) {
    teile.push(
      `${regeln} Regeln — ab hier lohnt Überarbeiten mehr als Anhängen`
    );
  }
  if (zeichen >= PROMPT_WARN_ZEICHEN) {
    teile.push(
      `${zeichen} Zeichen — der Rest des Kontextfensters trägt Kriterien, ` +
        `RAG-Treffer und Gesprächsverlauf, und Ollama schneidet still ab`
    );
  }
  return teile.join(" · ");
}

// ACHTUNG: hier stand bis zum 13.08. ein export promptMasse(content), das die
// beiden Zahlen zu einer Zeile verband ("11 Regeln · 1135 Zeichen"). Es hatte
// im ganzen Baum KEINEN Importeur -- beide Seiten setzen die Zeile selbst
// zusammen, weil sie die Teile einzeln einfaerben (amber bei ueberschrittener
// Schwelle) und je einen eigenen Tooltip daran haengen. Ein Helfer, der das
// nicht kann, wird nie gerufen.
//
// Gestrichen und nicht stehen gelassen: ein ungenutzter Export sieht wie ein
// dritter Weg zu denselben Zahlen aus. Wer ihn beim naechsten Umbau greift,
// verliert Faerbung und Tooltips, ohne dass ein Test faellt -- es gibt keinen,
// denn niemand hat ihn je benutzt.
